import { useQuery } from '@tanstack/vue-query'
import { apiRequest } from '@thyme/libs/src/api/apiRequest'
import { Method, RequestOptions } from '@thyme/libs/src/api/types/api'
import { hoursToMs } from '@thyme/libs/src/time/convert'
import { StrapiConfig } from './config'
import {
  ArticlesResponse,
  CmsListFn,
  Locale,
  StrapiStatus,
  StrapiListRequestOptions,
} from './types'

const CMS_ARTICLE_LIST_STALE_TIME_HOURS = 1
/**
 * Gets titles, slugs, and categories of all articles
 * @param config
 * @param client
 */
export async function listPublishedArticles(
  config: StrapiConfig,
  client: CmsListFn = strapiListApiRequest
): Promise<ArticlesResponse> {
  return (await client(config, '/articles', {
    params: {
      pagination: { pageSize: 300, page: 1 },
      locale: [Locale.ENGLISH],
      status: StrapiStatus.PUBLISHED,
      fields: ['title', 'slug', 'category_sort_order'],
      populate: {
        resource_category: {
          fields: ['name', 'sort_order'],
        },
        resource_topics: {
          fields: ['name'],
        },
        cancer_types: {
          fields: ['name'],
        },
        treatment_stages: {
          fields: ['name'],
        },
      },
    },
  })) as ArticlesResponse
}

export const createPublishedArticlesQueryFn = (config: StrapiConfig) => () =>
  listPublishedArticles(config)

export const selectArticlesData = (response: ArticlesResponse) => response.data

/**
 *
 * @param config
 */
export function listPublishedArticlesWithUseQuery(config: StrapiConfig) {
  const { data, isError, error, isLoading } = useQuery({
    queryKey: ['publishedArticles'],
    queryFn: createPublishedArticlesQueryFn(config),
    staleTime: hoursToMs(CMS_ARTICLE_LIST_STALE_TIME_HOURS),
    retry: false,
    select: selectArticlesData,
  })

  return { data, isError, error, isLoading }
}

/**
 *
 * @param config
 * @param method
 * @param endpoint
 * @param options
 */
export async function _strapiApiRequest(
  config: StrapiConfig,
  method: Method,
  endpoint: string,
  options: RequestOptions
): Promise<any> {
  if (!options.headers) {
    options.headers = {}
  }
  options.headers.Authorization = `Bearer ${config.apiKey}`
  // Cache-control isn't supported by the Strapi API
  options.noCache = true

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return await apiRequest(method, endpoint, options, config.apiUrl)
}

/**
 *
 * @param config
 * @param endpoint
 * @param options
 */
export async function strapiListApiRequest(
  config: StrapiConfig,
  endpoint: string,
  options: StrapiListRequestOptions
): Promise<any> {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return await _strapiApiRequest(config, 'GET', endpoint, options)
}
